import {
  CollectionsRounded,
  FileCopyRounded,
  PeopleOutlineOutlined,
  PersonOutlineOutlined,
  PlaylistPlayOutlined,
  QuizOutlined,
} from "@mui/icons-material";
import { IconButton, SxProps, Tooltip, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import FeedbackProvider from "components/FeedbackProvider";
import FeatureGate from "components/FeatureGate";
import ACCOUNT_FEATURE from "enums/ACCOUNT_FEATURE";
import {
  disabledFeatureTooltipSlotProps,
  disabledFeatureUpsellMessage,
} from "constants/DISABLED_FEATURE";
import { APP_ROUTES } from "enums/APP_ROUTES";
import TEST_ID from "enums/TEST_ID";
import AiIcon from "features/AiInsights/components/AiIcon";
import { LISTING_TYPE } from "features/VideoManagement/types";
import useGetProjectFeatures from "hooks/api/useGetProjectFeatures";
import useVidletNavigate from "hooks/navigation/useVidletNavigate";
import useCurrentUser from "hooks/useCurrentUser";
import useProject from "hooks/useProject";
import colors from "theme/base/colors";
import { flexCentered } from "theme/base/flex";
import { hasSuperAdminRole, hasViewerRole } from "utils/roles";
import HydraIcon from "../../components/Icons/HydraIcon";
import { getDataTestProp } from "utils/cypressTesting";

export default function Sidebar({ sx }: { sx?: SxProps }) {
  const { navigate } = useVidletNavigate();
  const { pathname } = location;
  const { role } = useCurrentUser();
  const { imported, projectId } = useProject();
  const { features } = useGetProjectFeatures(projectId);

  const hasFeature = (featureName: string) =>
    features.some((feature) => feature.featureName === featureName);

  const isViewer = hasViewerRole(role);
  const hydraRoleDisabled = !hasSuperAdminRole(role) || imported;
  const aiInsightsRoute = (APP_ROUTES as Record<string, string>).AI_INSIGHTS;

  const SIDEBAR_ROUTES: {
    title: string;
    route: string;
    icon;
    isDisabled?: boolean;
    disabledMessage?: string;
    catalogFeatureName?: string;
    isActiveTest?: string | ((pathname: string) => boolean);
    testId?: string;
  }[] = [
    {
      title: "Scripts",
      route: APP_ROUTES.SCRIPTS,
      icon: <QuizOutlined />,
      isDisabled: isViewer,
    },
    {
      title: "Participants",
      route: APP_ROUTES.PARTICIPANTS,
      icon: <PeopleOutlineOutlined />,
      isDisabled: isViewer,
    },
    {
      title: "Media",
      isActiveTest: (pathname: string) =>
        pathname.includes(`${APP_ROUTES.VIDEOS}/`) &&
        !pathname.includes(`${APP_ROUTES.VIDEOS}/${LISTING_TYPE.FILE}`),
      route: `${APP_ROUTES.VIDEOS}/all`,
      icon: <CollectionsRounded />,
    },
    {
      title: "Playlists",
      route: APP_ROUTES.PLAYLISTS,
      icon: <PlaylistPlayOutlined />,
      isDisabled: isViewer,
    },
    {
      title: "Documents",
      route: APP_ROUTES.DOCUMENTS,
      icon: <FileCopyRounded />,
      isDisabled: isViewer,
      testId: TEST_ID.documents.sidebarNav,
    },
    {
      title: "AI Insights",
      route: aiInsightsRoute,
      icon: <AiIcon />,
      isDisabled: !hasFeature(ACCOUNT_FEATURE.AI_INSIGHTS),
      disabledMessage: disabledFeatureUpsellMessage(
        ACCOUNT_FEATURE.AI_INSIGHTS,
      ),
      catalogFeatureName: ACCOUNT_FEATURE.AI_INSIGHTS,
    },
    {
      title: "Personas",
      route: APP_ROUTES.PERSONAS,
      icon: <PersonOutlineOutlined />,
      isDisabled: isViewer || !hasFeature(ACCOUNT_FEATURE.PERSONAS),
      disabledMessage: isViewer
        ? undefined
        : disabledFeatureUpsellMessage(ACCOUNT_FEATURE.PERSONAS),
      catalogFeatureName: isViewer ? undefined : ACCOUNT_FEATURE.PERSONAS,
      testId: TEST_ID.personas.sidebarNav,
    },
    {
      title: "Hydra",
      route: APP_ROUTES.HYDRA,
      icon: <HydraIcon />,
      isDisabled:
        hydraRoleDisabled || !hasFeature(ACCOUNT_FEATURE.PROJECT_SEARCH),
      disabledMessage:
        !hydraRoleDisabled && !hasFeature(ACCOUNT_FEATURE.PROJECT_SEARCH)
          ? disabledFeatureUpsellMessage(ACCOUNT_FEATURE.PROJECT_SEARCH)
          : undefined,
      catalogFeatureName: ACCOUNT_FEATURE.PROJECT_SEARCH,
    },
  ];

  function getColor(title: string) {
    return title === "Hydra"
      ? colors.vidletColors.hydra
      : imported
        ? colors.primary.imported
        : colors.primary.main;
  }

  return (
    <Stack sx={{ justifyContent: "space-between" }}>
      <Stack direction="column" sx={{ ...flexCentered, ...sx }} spacing={2}>
        {SIDEBAR_ROUTES.filter(
          (route) =>
            route.route &&
            (!route.isDisabled || Boolean(route.disabledMessage)),
        ).map(
          ({
            title,
            isActiveTest,
            route,
            icon,
            testId,
            isDisabled,
            disabledMessage,
            catalogFeatureName,
          }) => {
            const activeMatch =
              typeof isActiveTest === "function"
                ? isActiveTest(pathname)
                : isActiveTest && pathname.includes(isActiveTest);
            const isActive = activeMatch || pathname.includes(route);
            const navBody = (
              <Stack
                direction="column"
                sx={{
                  ...flexCentered,
                  transition: "margin .2s",
                }}
              >
                <IconButton
                  {...(testId ? getDataTestProp(testId) : {})}
                  disabled={isDisabled}
                  sx={{
                    backgroundColor: isActive ? getColor(title) : "#ddd",
                    color: !isActive ? getColor(title) : "common.white",
                    opacity: isDisabled ? 0.4 : !isActive ? 0.75 : 1,
                    borderRadius: ".5rem",
                    width: "2rem",
                    height: "2rem",
                    transform: isActive ? "scale(1.25)" : undefined,
                    transition:
                      "transform .2s ease-in-out, opacity .2s ease-in-out",
                    "&:hover": {
                      backgroundColor: colors.primary.light,
                      color: colors.white.main,
                    },
                  }}
                  onClick={() => navigate(route)}
                >
                  {icon}
                </IconButton>
                {isActive && (
                  <Typography
                    sx={{ color: getColor(title), fontSize: 10, mb: "-1rem" }}
                    variant="overline"
                  >
                    {title}
                  </Typography>
                )}
              </Stack>
            );

            const featureUpsellTooltip =
              isDisabled && disabledMessage && catalogFeatureName;
            const tooltip = (
              <Tooltip
                title={isDisabled ? (disabledMessage ?? title) : title}
                placement="right"
                slotProps={
                  featureUpsellTooltip
                    ? disabledFeatureTooltipSlotProps
                    : {
                        tooltip: {
                          sx: {
                            backgroundColor: getColor(title),
                            color: "white",
                          },
                        },
                        arrow: {
                          sx: {
                            color: getColor(title),
                          },
                        },
                      }
                }
              >
                {navBody}
              </Tooltip>
            );

            return (
              <span key={route}>
                {isDisabled && catalogFeatureName ? (
                  <FeatureGate
                    featureName={catalogFeatureName}
                    title={disabledMessage ?? ""}
                    placement="right"
                  >
                    {tooltip}
                  </FeatureGate>
                ) : (
                  tooltip
                )}
              </span>
            );
          },
        )}
        <FeedbackProvider
          sx={{
            position: "absolute",
            bottom: "1rem",
            left: ".75rem",
            zIndex: 10000,
          }}
        />
      </Stack>
    </Stack>
  );
}
